import type { TAEInfo } from '@/api/hcp/classification/hooks/useGetTAEInfoForHCP';
import type { ClinicalTrialsTotalCount } from '@/api/queries/hcp/scientificInformation/useGetHCPClinicalTrialsCount';
import type { PublicationsTotalCount } from '@/api/queries/hcp/scientificInformation/useGetPublications';
import type { Dayjs } from 'dayjs';
import type { HcpAssociation } from './association';
import type { Consents } from './consents';
import type { Donts } from './donts';
import type { HCO } from './hco';
import type { TaeScoring } from './taescoring';

export const BK_SEPERATOR = '^^';

export const buildHcpBk = (countryCode: string, uci: string): string => `${countryCode}${BK_SEPERATOR}${uci}`;
export const getCountryCode = (bk: string): string => {
  if (!bk) {
    throw new Error('getCountryCode: empty business key');
  } else if (!bk.includes(BK_SEPERATOR)) {
    throw new Error(`getCountryCode: invalid business key '${bk}'`);
  }
  return bk.split(BK_SEPERATOR)[0];
};
export const getUCI = (bk: string): string => bk.split(BK_SEPERATOR)[1];

export interface HCPBase {
  uci?: string | null;
  bk?: string | null;
  id?: string | null;
  person: Person;
  type: string | null;
  classification: string[];
  tae?: TAEInfo[];
  speciality?: string | null;
  country: string | null;
  countryCode?: string | null;
  contact: Contact;
  nationalId?: string | null;
  hcp_consent_profiling_business_relation_flag: string | null;
  title: string | null;
  hcp_consent_profiling_communication_flag: string | null;
  consent_disclosure: string | null;
  consent_do_not_call: string | null;
  hcp_mdm_id: string | null;
  primaryPointOfContact: string | null;
  primaryPointOfContactEmail: string | null;
  hcpRegistrationCompleteFlag: boolean;
  websiteRegistrationDate?: string;
}

export interface HCPExtended extends HCPBase {
  hcoLocations?: HCO[];
  associations?: HcpAssociation[];
  socialMedia?: SocialMedia;
  consents?: Consents;
  donts?: Donts;
  activitysores?: TaeScoring[];
  topicscores?: TaeScoring[];
  clinicaltrialsponsor?: TaeScoring[];
  pubmed?: {
    link?: string;
    totalResults?: number;
  };
  sponsoredTrials?: {
    link?: string;
    totalResults?: number;
  };
  crmLink?: string | null;
  hco_crm_link?: string;
  clinicalTrialsTotalCount?: ClinicalTrialsTotalCount;
  publicationsTotalCount?: PublicationsTotalCount;
}

export interface HCP extends HCPBase {
  primaryLocation?: HCO | null;
  latestInteraction?: Dayjs | null;
  tae?: TAEInfo[];
  accountPlanInProgressCount: number | null;
}

export enum Country {
  UK = 'United Kingdom',
  France = 'France',
  Germany = 'Germany',
  Finland = 'Finland',
  Australia = 'Austria',
}

export type Person = {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  age?: number | null;
  titles?: string[];
};

type SocialMedia = {
  twitter?: string;
  linkedIn?: string;
};

type Contact = {
  emails: string[];
  phoneNumbers: string[];
  linkedIn?: string | null;
};

export type HcpSearchObject = {
  firstName: string;
  lastName: string;
  uci: string;
  email: string;
  hco: string;
  nationalId: string;
  specialty: string[];
  country: string[];
};

export type HcpCTAndPubmedCount = {
  clinicalTrials: number;
  pubmed: number;
};
